import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'url';
import config from 'config';
import Button from '@mulesoft/anypoint-components/lib/Button';
import Tooltip from '@mulesoft/anypoint-components/lib/Tooltip';
import { Table } from '@mulesoft/exchange-ui-components';
import {
  dateFormat,
  ScanConfigurationPropType
} from '~/components/AgentScanners/types/commonTypes';
import { getPath } from '~/utils/routes';
import { formatRelativeTime } from '../../utils/utils';
import { formatDate } from '~/utils/formatDate';
import ScanConfigurationActions from '../ScanConfigurationActions';
import Pagination from './Pagination';
import Header from './Header';
import Status from './Status';
import StatusRun from './StatusRun';
import styles from './ScanConfigurationsList.css';

const getHeaders = () => {
  const headers = [
    {
      key: 'status',
      label: 'Scanner status'
    },
    {
      key: 'name',
      label: 'Scanner name'
    },
    {
      key: 'target',
      label: 'Target system'
    },
    {
      key: 'lastCompletedRun',
      label: 'Last completed run'
    },
    {
      key: 'lastRunStatus',
      label: 'Last run status'
    },
    {
      key: 'nextRun',
      label: 'Next run'
    },
    {
      key: 'totalAgents',
      label: 'Total agents'
    },
    {
      key: 'actions'
    }
  ];

  return headers;
};

function renderEmptyState() {
  const emptyImageURL = format({
    ...config.images.uri,
    pathname: 'empty-list-agent-scanner.png'
  });
  const addScannerPath = getPath('agentScannersAdd');

  return (
    <div className={styles.emptyState} data-test-id="empty-state">
      <img
        src={emptyImageURL}
        alt="Empty Agent Scanners"
        className={styles.emptyStateImage}
      />
      <p className={styles.emptyStateDescription}>
        Add an Agent Scanner to discover and external systems to find Agents to
        import into Exchange
      </p>
      <div className={styles.emptyStateButtonGroup}>
        <Button
          testId="empty-state-import-agentforce-button"
          kind="secondary"
          onClick={() => {}}
        >
          Import Agentforce agents
        </Button>
        <Link to={addScannerPath}>
          <Button testId="empty-state-add-scanner-button" kind="primary">
            Add Scanner
          </Button>
        </Link>
      </div>
    </div>
  );
}

function scanConfigurationName(scanConfig, getPathFn, onClick) {
  const path = getPathFn && getPathFn('scanConfigHome', scanConfig);

  const handleClick = (e) => {
    e.preventDefault();
    if (onClick) {
      onClick(scanConfig);
    }
  };

  // If we have an onClick handler, make it clickable even without a path
  if (onClick) {
    return (
      <div className={styles.name}>
        <button
          type="button"
          className={styles.nameButton}
          onClick={handleClick}
        >
          <span className="visually-hidden">Go to Scan Configuration</span>
          {scanConfig.name}
        </button>
      </div>
    );
  }

  // Render Link if path is available
  if (path) {
    return (
      <div className={styles.name}>
        <Link to={path}>
          <span className="visually-hidden">Go to Scan Configuration</span>
          {scanConfig.name}
        </Link>
      </div>
    );
  }

  // Fallback: just text, not clickable
  return (
    <div className={styles.name}>
      <span>{scanConfig.name}</span>
    </div>
  );
}

function scanConfigurationTargetSystem(scanConfig) {
  const targetSystemDescription =
    scanConfig.connection?.targetSystem?.name || '-';

  return <div className={styles.text}>{targetSystemDescription}</div>;
}

function renderDateWithTooltip(dateString, testIdPrefix) {
  if (!dateString) {
    return <span data-test-id={`scan-config-${testIdPrefix}`}>-</span>;
  }

  const relativeTime = formatRelativeTime(dateString);
  const formattedDate = formatDate(dateString, dateFormat);

  return (
    <Tooltip
      content={formattedDate}
      position="top"
      testId={`scan-config-${testIdPrefix}-tooltip`}
    >
      <span data-test-id={`scan-config-${testIdPrefix}`}>{relativeTime}</span>
    </Tooltip>
  );
}

function renderLastCompletedRun(dateString) {
  return renderDateWithTooltip(dateString, 'last-run');
}

function renderNextRun(dateString) {
  return renderDateWithTooltip(dateString, 'next-run');
}

function calculateTotalAgents(scanConfig) {
  if (!scanConfig?.lastSuccessfulRunSummary) {
    return 0;
  }

  try {
    // Parse the JSON string (handles escaped quotes automatically)
    const summary = JSON.parse(scanConfig.lastSuccessfulRunSummary);

    // Extract stats object
    const stats = summary?.stats;

    if (!stats) {
      console.warn('Stats object not found in lastSuccessfulRunSummary');

      return 0;
    }

    // Sum added + updated + unchanged
    const added = Number(stats.added) || 0;
    const updated = Number(stats.updated) || 0;
    const unchanged = Number(stats.unchanged) || 0;

    return added + updated + unchanged;
  } catch (error) {
    console.error('Failed to parse lastSuccessfulRunSummary JSON:', error);

    return 0;
  }
}

function mapScanConfigurations(
  scanConfigurations,
  getPathFn,
  onClick,
  onRunClick,
  onEditClick,
  onDeleteClick
) {
  return scanConfigurations.map((scanConfig) => {
    const scanConfigRow = {
      status: <Status status={scanConfig.state} />,
      name: scanConfigurationName(scanConfig, getPathFn, onClick),
      target: scanConfigurationTargetSystem(scanConfig),
      lastCompletedRun: renderLastCompletedRun(scanConfig.lastSuccessfulRunAt),
      lastRunStatus: (
        <StatusRun
          status={scanConfig.lastRunStatus}
          statusDetail={scanConfig.lastRunStatusDetail || ''}
        />
      ),
      nextRun: renderNextRun(scanConfig.nextRunAt),
      totalAgents: (
        <div className={styles.text}>{calculateTotalAgents(scanConfig)}</div>
      ),
      actions: (
        <ScanConfigurationActions
          onRun={() => onRunClick(scanConfig)}
          onEdit={() => onEditClick(scanConfig.id)}
          onDelete={() => onDeleteClick(scanConfig.id)}
        />
      )
    };

    return scanConfigRow;
  });
}

const ScanConfigurationsList = ({
  id = '',
  scanConfigurations = [],
  scannersError = null,
  organizationId = null,
  onLoadScanConfigurations = () => {},
  onScanConfigurationClick = () => {},
  currentPage = 1,
  itemsPerPage = 3,
  onPageChange = () => {},
  onRunNowClick = () => {},
  onEditScannerClick = () => {},
  onDeleteScannerClick = () => {}
}) => {
  if (scannersError) {
    return (
      <div className={styles.errorState}>
        <h3>Error loading scanners</h3>
        <p>
          {scannersError.message ||
            'An error occurred while loading the scanners.'}
        </p>
        <Button
          kind="primary"
          onClick={() => onLoadScanConfigurations(organizationId)}
        >
          Retry
        </Button>
      </div>
    );
  }

  // Calculate pagination
  const totalItems = scanConfigurations.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedScanConfigurations = scanConfigurations.slice(
    startIndex,
    endIndex
  );

  return scanConfigurations.length > 0 ? (
    <div>
      <Header />
      <div
        id={id}
        className={styles.scanConfigurationsList}
        data-test-id="scan-configurations-list"
      >
        <Table
          caption="Scan Configurations results"
          testId="scanConfigurations"
          className={styles.table}
          headers={getHeaders()}
          rows={mapScanConfigurations(
            paginatedScanConfigurations,
            getPath,
            onScanConfigurationClick,
            onRunNowClick,
            onEditScannerClick,
            onDeleteScannerClick
          )}
          emptyMessage="We couldn't find results that match your criteria."
        />
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          testId="scan-configurations-pagination"
        />
      )}
    </div>
  ) : (
    renderEmptyState()
  );
};

ScanConfigurationsList.propTypes = {
  id: PropTypes.string,
  scanConfigurations: PropTypes.arrayOf(ScanConfigurationPropType),
  scannersError: PropTypes.object,
  organizationId: PropTypes.string,
  onLoadScanConfigurations: PropTypes.func,
  onScanConfigurationClick: PropTypes.func,
  currentPage: PropTypes.number,
  itemsPerPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onRunNowClick: PropTypes.func,
  onEditScannerClick: PropTypes.func,
  onDeleteScannerClick: PropTypes.func
};

export { ScanConfigurationsList };

export default ScanConfigurationsList;
